import React, { useCallback, useEffect, useState } from "react";
import {
  getTablePaymentConfig,
  prepareTablePayment,
  getTablePaymentStatus,
  issueTablePaymentTicket,
  cancelTablePayment,
  loadNicepaySdk,
  openNicepayPay,
} from "../api/qrOrder";

/**
 * 테이블오더 NICEPAY 결제 패널.
 * PENDING/PARTIALLY_PAID 주문에서만 렌더(부모가 조건부로 렌더). 결제 활성화(config.enabled)가
 * 아니면 아무것도 그리지 않는다.
 *
 * 동작:
 *  - 아직 결제 티켓이 없으면(READY 0건): "전액 결제" 또는 "N빵 나눠내기(N명)" 선택 → prepare 로
 *    결제 건 생성 → 상태 재조회.
 *  - READY 결제 건이 있으면 각 건마다 "결제" 버튼. 누르면 NICEPAY 결제창을 띄운다(전체 페이지 전환).
 *    분할결제는 각자(공유 테이블의 일행이 자기 기기로) 자기 몫 버튼을 눌러 결제하면 된다.
 *  - 인증/승인은 서버 콜백에서 처리되고 SPA 결과페이지로 돌아오므로, 돌아온 뒤 "새로고침"으로
 *    최신 결제현황을 반영한다(SSE 로도 갱신됨).
 */
export default function TableOrderPayment({ qrToken, order, onPaid }) {
  const tableOrderId = order.tableOrderId;
  const total = Number(order.totalAmount) || 0;

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState("");
  const [splitN, setSplitN] = useState(2);

  // 상태 조회만 한다. 여기서 부모(onPaid=주문내역 재조회)를 호출하면, PAID 주문에도 패널이 뜨는 지금 구조상
  // 조회→재조회 무한 루프(주문내역 화면 빠른 새로고침)가 발생하므로 호출하지 않는다. 부모 갱신은
  // 취소(doCancel) 등 명시적 동작에서만 하고, 결제완료 반영은 SSE(PAYMENT_*)로 처리된다.
  const refreshStatus = useCallback(async () => {
    try {
      const res = await getTablePaymentStatus(qrToken, tableOrderId);
      setStatus(res.data);
    } catch {
      /* 조용히 무시 - 다음 새로고침에 반영 */
    }
  }, [qrToken, tableOrderId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getTablePaymentConfig();
        if (!alive) return;
        const cfg = res.data;
        setConfig(cfg);
        if (cfg && cfg.enabled && cfg.jsSdkUrl) {
          loadNicepaySdk(cfg.jsSdkUrl).catch(() => {});
        }
      } catch {
        /* 설정 조회 실패 시 결제 패널을 숨긴다 */
      }
    })();
    refreshStatus();
    return () => {
      alive = false;
    };
  }, [refreshStatus]);

  if (!config || !config.enabled) return null;

  const paid = Number(status?.paidAmount) || 0;
  const remaining = Math.max(0, total - paid);
  const payments = status?.payments || [];
  const readyRows = payments.filter((p) => p.paymentStatus === "READY");
  const doneRows = payments.filter((p) => p.paymentStatus === "DONE");

  const doPrepare = async (splits) => {
    setBusy(true);
    setError("");
    try {
      await prepareTablePayment(qrToken, tableOrderId, "card", splits);
      await refreshStatus();
    } catch (e) {
      setError(e.response?.data?.message || "결제 준비에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const payFull = () => doPrepare([]); // 전액 단건

  const paySplit = () => {
    const n = Math.max(2, Math.min(10, Number(splitN) || 2));
    const base = Math.floor(remaining / n);
    const splits = [];
    let acc = 0;
    for (let i = 1; i <= n; i += 1) {
      const amt = i === n ? remaining - acc : base; // 마지막 사람이 나머지(원 단위) 부담
      acc += amt;
      splits.push({ payerSeq: i, payerLabel: `${i}/${n}`, amount: amt });
    }
    doPrepare(splits);
  };

  const payRow = async (row) => {
    setError("");
    try {
      await loadNicepaySdk(config.jsSdkUrl);
      const res = await issueTablePaymentTicket(qrToken, row.pgOrderId);
      openNicepayPay(res.data); // goPay(form) → 인증 성공 시 전체 페이지가 서버 콜백으로 전환됨
    } catch (e) {
      setError(e.response?.data?.message || e.message || "결제창을 열 수 없습니다.");
    }
  };

  const doCancel = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("이 주문의 결제를 취소(환불)할까요?")) return;
    setCanceling(true);
    setError("");
    try {
      await cancelTablePayment(qrToken, tableOrderId, "주문취소");
      await refreshStatus();
      if (typeof onPaid === "function") onPaid(); // 주문내역 갱신(취소되면 목록에서 빠짐)
    } catch (e) {
      setError(e.response?.data?.message || e.message || "결제 취소에 실패했습니다.");
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="to-pay">
      <div className="to-pay-title">
        결제 {paid.toLocaleString()} / {total.toLocaleString()}원
        {remaining > 0 ? ` · 남은 금액 ${remaining.toLocaleString()}원` : " · 결제완료"}
      </div>
      {error && <div className="to-pay-error">{error}</div>}

      {remaining > 0 && readyRows.length === 0 && (
        <div className="to-pay-choose">
          <button type="button" className="to-pay-btn" disabled={busy} onClick={payFull}>
            {busy ? "준비 중..." : "전액 결제"}
          </button>
          <div className="to-pay-split">
            <span>또는 나눠내기</span>
            <select value={splitN} onChange={(e) => setSplitN(Number(e.target.value))} disabled={busy}>
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}명</option>
              ))}
            </select>
            <button type="button" className="to-pay-btn secondary" disabled={busy} onClick={paySplit}>
              N빵 결제 준비
            </button>
          </div>
        </div>
      )}

      {readyRows.length > 0 && (
        <div className="to-pay-shares">
          {readyRows.map((row) => (
            <button key={row.pgOrderId} type="button" className="to-pay-share-btn" onClick={() => payRow(row)}>
              {row.payerLabel || "결제"} · {Number(row.amount).toLocaleString()}원 결제하기
            </button>
          ))}
        </div>
      )}

      {doneRows.length > 0 && (
        <div className="to-pay-done-list">
          {doneRows.map((row) => (
            <div key={row.pgOrderId} className="to-pay-done-row">
              ✓ {row.payerLabel ? `${row.payerLabel} ` : ""}{Number(row.amount).toLocaleString()}원 결제완료
            </div>
          ))}
          <button type="button" className="to-pay-cancel-btn" disabled={canceling} onClick={doCancel}>
            {canceling ? "취소 처리 중..." : "결제 취소(환불)"}
          </button>
        </div>
      )}

      <button type="button" className="to-pay-refresh" onClick={refreshStatus}>
        결제현황 새로고침
      </button>
    </div>
  );
}
